import { useEffect, useState, type ReactElement } from 'react';
import { api, type BoxSettingsView } from '../api';
import { Sheet } from './Sheet';
import { useNachgeliefert } from '../nachgeliefert';

/**
 * The Box credentials, in the browser.
 *
 * They used to live in environment variables, which meant every correction was
 * a redeploy of the whole stack — and no way to find out whether a value was
 * right except to create a project and watch it fail. The secret is stored
 * encrypted and never sent back here; an empty field means "leave it alone".
 */
export function BoxSettingsSheet({ onClose }: { onClose: () => void }): ReactElement {
  const [view, setView] = useState<BoxSettingsView | null>(null);
  const [form, setForm, formNachliefern] = useNachgeliefert({
    clientId: '',
    clientSecret: '',
    subjectType: 'enterprise' as 'enterprise' | 'user',
    subjectId: '',
    developerToken: '',
    rootFolderId: '0',
    webhookPrimaryKey: '',
    webhookSecondaryKey: '',
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const { box } = await api.boxSettings();
      setView(box);
      formNachliefern((f) => ({
        ...f,
        clientId: box.clientId,
        subjectType: box.subjectType === 'user' ? 'user' : 'enterprise',
        subjectId: box.subjectId,
        rootFolderId: box.rootFolderId,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Die Einstellungen konnten nicht geladen werden.');
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const run = async (what: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await what();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Das hat nicht geklappt.');
    } finally {
      setBusy(false);
    }
  };

  return (
    // „Zurück“ statt „Fertig“: hierher kommt man aus der Ablage, und dorthin
    // geht es zurück (7.23).
    <Sheet title="Box-Zugang" onClose={onClose} closeLabel="Zurück">
      {error ? <p className="error-text">{error}</p> : null}

      <div className="card" style={{ margin: '0 0 14px' }}>
        <div className="spread">
          <div>
            <div className="title">
              {view?.source === 'datenbank'
                ? 'Hier eingetragen'
                : view?.source === 'umgebung'
                  ? 'Aus den Umgebungsvariablen'
                  : 'Noch nicht eingerichtet'}
            </div>
            <div className="sub">
              {view?.hasDeveloperToken
                ? 'Entwickler-Token hinterlegt'
                : view?.hasClientSecret
                  ? 'Client-Secret hinterlegt'
                  : 'Kein Geheimnis hinterlegt'}
            </div>
          </div>
          <span className={`badge ${view?.source === 'keine' ? 'err' : 'ok'}`}>
            {view?.source === 'keine' ? 'fehlt' : 'da'}
          </span>
        </div>
        {view?.unreadable ? (
          <p className="hint" style={{ color: 'var(--err-text)' }}>
            Das gespeicherte Geheimnis lässt sich nicht mehr entschlüsseln. Bitte neu eintragen.
          </p>
        ) : null}
      </div>

      <label className="field">
        <span>Client-ID</span>
        <input value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })} autoCapitalize="none" />
      </label>
      <label className="field">
        <span>Client-Secret</span>
        <input
          type="password"
          value={form.clientSecret}
          onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
          placeholder={view?.hasClientSecret ? '•••••••• (bleibt, wenn leer)' : ''}
          autoCapitalize="none"
        />
      </label>
      <label className="field">
        <span>Art des Dienstkontos</span>
        <select
          value={form.subjectType}
          onChange={(e) => setForm({ ...form, subjectType: e.target.value as 'enterprise' | 'user' })}
        >
          <option value="enterprise">Enterprise (üblich)</option>
          <option value="user">Einzelner Benutzer</option>
        </select>
      </label>
      <label className="field">
        <span>Enterprise- bzw. Benutzer-ID</span>
        <input value={form.subjectId} onChange={(e) => setForm({ ...form, subjectId: e.target.value })} inputMode="numeric" />
      </label>
      <label className="field">
        <span>Ordner für den Verbindungstest</span>
        <input
          value={form.rootFolderId}
          onChange={(e) => setForm({ ...form, rootFolderId: e.target.value })}
          inputMode="numeric"
        />
        <p className="hint">0 ist der Wurzelordner des Dienstkontos.</p>
      </label>

      {/* Without these, nobody can tell a delivery from Box apart from anyone
          else who found the address -- so without them no hook is set. */}
      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Webhook-Schlüssel</h3>
      <p className="hint" style={{ marginTop: 0 }}>
        Aus der Box-App unter „Webhooks“ (Primary und Secondary Key). Adresse für Box: <code>{`${window.location.origin}/api/box/webhook`}</code>
      </p>
      <label className="field">
        <span>Primary Key</span>
        <input
          type="password"
          value={form.webhookPrimaryKey}
          onChange={(e) => setForm({ ...form, webhookPrimaryKey: e.target.value })}
          placeholder={view?.hasWebhookKeys ? '•••••••• (bleibt, wenn leer)' : ''}
          autoCapitalize="none"
        />
      </label>
      <label className="field">
        <span>Secondary Key</span>
        <input
          type="password"
          value={form.webhookSecondaryKey}
          onChange={(e) => setForm({ ...form, webhookSecondaryKey: e.target.value })}
          placeholder={view?.hasWebhookKeys ? '•••••••• (bleibt, wenn leer)' : ''}
          autoCapitalize="none"
        />
      </label>

      {result ? (
        <p className="hint" style={{ color: result.ok ? 'var(--ok-text)' : 'var(--err-text)' }}>{result.message}</p>
      ) : null}

      <div className="stack">
        <button
          disabled={busy}
          onClick={() => void run(async () => setResult(await api.testBoxSettings(form)))}
        >
          {busy ? 'Prüfe…' : 'Verbindung prüfen'}
        </button>
        <button
          className="primary"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              const { box } = await api.saveBoxSettings(form);
              setView(box);
              setForm({ ...form, clientSecret: '', developerToken: '', webhookPrimaryKey: '', webhookSecondaryKey: '' });
              setResult(await api.testBoxSettings({}));
            })
          }
        >
          Speichern und prüfen
        </button>
        {view?.source === 'datenbank' ? (
          <button
            className="ghost"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const { box } = await api.clearBoxSettings();
                setView(box);
                setResult(null);
                await load();
              })
            }
          >
            Hier Eingetragenes verwerfen, wieder Umgebungsvariablen nehmen
          </button>
        ) : null}
      </div>

      <p className="hint" style={{ marginTop: 16 }}>
        Verschlüsselt gespeichert, nie wieder angezeigt. Gilt für alle Projekte.
      </p>
    </Sheet>
  );
}
